// Review Inbox (PLAN §5 step 6b–7, §6; task 21): uploads committed without review ("review later": every claim
// written `unreviewed`), auto-matches waiting for a person to confirm or detach, the filters the inbox offers, and
// the key that folds the same entity across the files of a batch into one card. Pure functions shared by the API,
// the pipeline, the web app and the tests.

import { normalizeName } from './names';
import { DETECTED_KINDS, type DetectedKind } from './uploads';

/**
 * An entity of a skipped review attached to an existing entity on its pre-selected match (same script, score ≥
 * 0.92): `pending` until someone confirms it or detaches it (which creates a separate entity).
 */
export const AUTO_MATCH_STATES = ['pending', 'confirmed', 'detached'] as const;
export type AutoMatchState = (typeof AUTO_MATCH_STATES)[number];

/** How old an item is, for the inbox filter. `older` = more than 30 days. */
export const INBOX_AGES = ['any', 'day', 'week', 'month', 'older'] as const;
export type InboxAge = (typeof INBOX_AGES)[number];

export const INBOX_AGE_LABELS: Record<InboxAge, string> = {
  any: 'Any time',
  day: 'Last 24 hours',
  week: 'Last 7 days',
  month: 'Last 30 days',
  older: 'Older than 30 days',
};

export const isInboxAge = (v: unknown): v is InboxAge => typeof v === 'string' && (INBOX_AGES as readonly string[]).includes(v);

const DAY = 86_400_000;

/** The time window an age filter selects, as epoch ms bounds (either may be absent). */
export function ageWindow(age: InboxAge, now: number): { after?: number; before?: number } {
  if (age === 'day') return { after: now - DAY };
  if (age === 'week') return { after: now - 7 * DAY };
  if (age === 'month') return { after: now - 30 * DAY };
  if (age === 'older') return { before: now - 30 * DAY };
  return {};
}

export const inAgeWindow = (at: number, w: { after?: number; before?: number }) =>
  (w.after === undefined || at >= w.after) && (w.before === undefined || at < w.before);

/** Inbox filters (PLAN §6: by upload, uploader, type, age). An upload is a batch of files sent together. */
export interface InboxFilter {
  batch?: string;
  uploader?: string;
  /** The document's detected kind (profile sheet, transcript…); every section filters on the file its items come from. */
  kind?: string;
  age?: InboxAge;
}

export const isDetectedKind = (v: unknown): v is DetectedKind => typeof v === 'string' && (DETECTED_KINDS as readonly string[]).includes(v);

export function parseInboxFilter(q: Record<string, string | undefined>): InboxFilter {
  const f: InboxFilter = {};
  if (q.batch) f.batch = q.batch;
  if (q.uploader) f.uploader = q.uploader;
  if (q.kind) f.kind = q.kind;
  if (isInboxAge(q.age) && q.age !== 'any') f.age = q.age;
  return f;
}

/**
 * The key that folds one entity found in several files of a batch into one card (PLAN §5 step 6b): the existing
 * entity it is matched or pre-selected to, else its kind and folded name ("ELNET", "Elnet" and "E.L.N.E.T." are
 * not folded together: only case, accents and punctuation between words are ignored).
 */
export function batchEntityKey(c: { kind: string; name: string; entityId?: string | null }): string {
  if (c.entityId) return `e:${c.entityId}`;
  return `n:${c.kind}:${normalizeName(c.name)}`;
}

/** Plain-language count ("1 file", "34 files"). */
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
